import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatPrice } from '../utils/formatPrice';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise, createPaymentIntentFn, listPaymentMethodsFn, formatStripeDeclineError } from '../services/stripe';
import type { SavedCard } from '../services/stripe';
import { createOrder } from '../services/orders';
import type { Order } from '../types/order';
import BackButton from './BackButton';
import { useUserIdentity } from '../hooks/useUserIdentity';

// ── Payment section (same pattern as AutoRenewConfirmModal) ───────────────────

interface PaymentHandle {
  confirmPayment: (coachId: string, offeringId: string, amountCents: number) => Promise<string>;
}

interface PaymentInnerProps {
  disabled: boolean;
  savedCard?: SavedCard;
  onUseDifferentCard?: () => void;
}

const PaymentInner = forwardRef<PaymentHandle, PaymentInnerProps>(({ disabled, savedCard, onUseDifferentCard }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');

  useImperativeHandle(ref, () => ({
    async confirmPayment(coachId, offeringId, amountCents) {
      if (!stripe || !elements) throw new Error('Stripe not ready');

      if (savedCard) {
        try {
          const { data } = await createPaymentIntentFn({ coachId, offeringId, amountCents, paymentMethodId: savedCard.id });
          if (data.alreadyConfirmed) return data.paymentIntentId;
          if (!data.clientSecret) throw new Error('No client secret returned');
          const result = await stripe.confirmCardPayment(data.clientSecret);
          if (result.error) {
            throw new Error(formatStripeDeclineError(result.error.decline_code, result.error.message));
          }
          return result.paymentIntent!.id;
        } catch (err) {
          // Cloud Function encodes decline_code as "decline:<code>" in the message.
          const msg = (err as Error).message || '';
          const declineCode = msg.startsWith('decline:') ? msg.slice('decline:'.length) : undefined;
          if (declineCode) throw new Error(formatStripeDeclineError(declineCode, undefined));
          throw err;
        }
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not mounted');
      const { data } = await createPaymentIntentFn({ coachId, offeringId, amountCents });
      if (!data.clientSecret) throw new Error('No client secret returned');
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: { card: cardElement },
      });
      if (result.error) {
        throw new Error(formatStripeDeclineError(result.error.decline_code, result.error.message));
      }
      return result.paymentIntent!.id;
    },
  }), [savedCard, stripe, elements]);

  if (savedCard) {
    return (
      <div>
        <div className="border border-db-border rounded-xl overflow-hidden mb-2">
          <div className="px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="text-[13px] font-semibold text-db-text-1 capitalize">{savedCard.brand}</span>
              <span className="text-[13px] text-db-text-2">····{savedCard.last4}</span>
              <span className="text-[12px] text-db-text-3">{savedCard.expMonth}/{savedCard.expYear}</span>
            </div>
            {onUseDifferentCard && !disabled && (
              <button
                type="button"
                onClick={onUseDifferentCard}
                className="text-[12px] text-db-accent-dark font-semibold hover:underline"
              >
                Use Different Card
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="border border-db-border rounded-xl overflow-hidden mb-2">
        <div className={`px-4 py-3.5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: '#111827',
                  fontFamily: 'system-ui, sans-serif',
                  '::placeholder': { color: '#9AABB8' },
                },
                invalid: { color: '#DC2626' },
              },
              hidePostalCode: true,
            }}
            onChange={(e) => setCardError(e.error?.message ?? '')}
          />
        </div>
        {cardError && (
          <div className="px-4 pb-3 text-[12px] text-red-600">{cardError}</div>
        )}
      </div>
    </div>
  );
});
PaymentInner.displayName = 'OrScreenPaymentSection';

// ── Check icon ────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A6B8A" strokeWidth="2.5" className="flex-shrink-0 mt-0.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface PlayerOrScreenProps {
  order: Order;
  onBack: () => void;
}

export default function PlayerOrScreen({ order, onBack }: PlayerOrScreenProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const paymentRef = useRef<PaymentHandle | null>(null);

  const [expandedCard, setExpandedCard] = useState<'one-time' | 'recurring' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultCard, setDefaultCard] = useState<SavedCard | null>(null);
  const [useSavedCard, setUseSavedCard] = useState(true);

  useEffect(() => {
    listPaymentMethodsFn()
      .then(({ data }) => {
        const def = data.paymentMethods.find(c => c.isDefault) ?? data.paymentMethods[0] ?? null;
        setDefaultCard(def);
      })
      .catch(() => { /* no saved cards */ });
  }, []);

  // Reset "use different card" toggle whenever the expanded card changes
  useEffect(() => {
    setUseSavedCard(true); // eslint-disable-line react-hooks/set-state-in-effect
    setError(null);
  }, [expandedCard]);

  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const livePlayer = useUserIdentity(order.playerId, 'player');
  const coachName = liveCoach?.name ?? order.coachName;
  const coachFirstName = coachName.split(' ')[0];
  const totalLessons = order.totalLessons ?? 4;
  const playerFirstName = (livePlayer?.name ?? order.playerName)?.split(' ')[0] ?? '';
  const priceCents = order.priceCents ?? 0;
  const totalChargeCents = priceCents * totalLessons;
  const priceStr = formatPrice(totalChargeCents);
  const priceStrLong = formatPrice(totalChargeCents);

  const handleExpand = (card: 'one-time' | 'recurring') => {
    setExpandedCard(prev => prev === card ? null : card);
  };

  const handleConfirm = async (isRecurring: boolean) => {
    if (!currentUser || !paymentRef.current) return;
    setSubmitting(true);
    setError(null);
    try {
      const paymentIntentId = await paymentRef.current.confirmPayment(
        order.coachId,
        order.offeringId,
        totalChargeCents,
      );
      await createOrder({
        type: 'training_program',
        offeringId: order.offeringId,
        offeringTitle: order.offeringTitle,
        playerId: currentUser.uid,
        coachId: order.coachId,
        priceCents,
        paymentIntentId,
        paymentStatus: 'captured',
        ...(order.platformFeeCents !== undefined && { platformFeeCents: order.platformFeeCents }),
        totalLessons,
        reserveFirstLesson: true,
        ...(isRecurring && { isRecurringPlan: true }),
      });
      navigate('/lessons');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Inline confirm section shared between both cards ─────────────────────────

  const confirmSection = (isRecurring: boolean) => (
    <div className="border-t border-db-border mt-5 pt-5 space-y-3">
      <div className="text-[13px] font-bold text-db-text-1">Confirm payment</div>

      <Elements stripe={stripePromise}>
        <PaymentInner
          ref={paymentRef}
          disabled={submitting}
          savedCard={useSavedCard && defaultCard ? defaultCard : undefined}
          onUseDifferentCard={defaultCard ? () => setUseSavedCard(false) : undefined}
        />
      </Elements>

      <p className="text-[12px] text-db-text-2 leading-relaxed">
        {isRecurring
          ? `You will be charged ${priceStrLong} for ${totalLessons} lessons. Auto-renews every ${totalLessons} lessons. Cancel any time.`
          : `You will be charged ${priceStrLong} for ${totalLessons} lessons with ${coachName}.`}
      </p>

      {error && (
        <div className="text-[12px] text-red-600 bg-red-50 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex gap-2.5">
        <button
          onClick={() => setExpandedCard(null)}
          disabled={submitting}
          className="flex-1 py-2.5 border border-db-border rounded-xl text-[13px] font-semibold text-db-text-2 hover:bg-db-bg disabled:opacity-40 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => handleConfirm(isRecurring)}
          disabled={submitting}
          className="flex-1 py-2.5 bg-biscayne rounded-xl text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
        >
          {submitting ? (
            <>
              <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing…
            </>
          ) : `Confirm · ${priceStr}`}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-full bg-db-bg items-center py-8 pt-5 px-4 md:py-14 md:pt-14 md:px-6">

      <div className="w-full max-w-[740px] mb-4">
        <BackButton onClick={onBack} fallback="/lessons" size="sm" flush sticky />
      </div>

      {/* Headline */}
      <div className="text-center mb-2">
        <h1 className="text-[30px] font-extrabold text-db-text-1 leading-tight">
          Nice work{playerFirstName ? `, ${playerFirstName}` : ''}.
        </h1>
      </div>
      <p className="text-center text-[15px] text-db-text-1 max-w-[460px] mb-11 leading-relaxed">
        You just completed your {totalLessons}-lesson Training Program with{' '}
        <strong className="text-db-text-1">{coachName}</strong>. Here's what's next.
      </p>

      {/* OR Cards */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-5 w-full max-w-[740px] items-stretch md:items-start">

        {/* Card A — One-time */}
        <div className="w-full md:flex-1 bg-white rounded-xl p-5 md:p-[30px_28px] shadow-sm border-2 border-db-border flex flex-col">
          <div className="text-[12px] font-bold uppercase tracking-[.08em] text-db-text-3 mb-3.5">One-time</div>
          <div className="text-[20px] font-extrabold text-db-text-1 mb-1.5">Continue with {coachFirstName}</div>
          <p className="text-[13px] text-db-text-1 mb-5 leading-relaxed">
            Book another {totalLessons}-lesson Training Program and pick up right where you left off.
          </p>
          <div className="flex flex-col gap-2.5 mb-6">
            <div className="flex items-start gap-2.5"><CheckIcon /><span className="text-[13px] text-db-text-1">{totalLessons} personalized video lessons</span></div>
            <div className="flex items-start gap-2.5"><CheckIcon /><span className="text-[13px] text-db-text-1">Progress report after lesson {totalLessons}</span></div>
            <div className="flex items-start gap-2.5"><CheckIcon /><span className="text-[13px] text-db-text-1">No commitment, book once</span></div>
          </div>
          {totalChargeCents > 0 && (
            <div className="text-[26px] font-extrabold text-db-text-1 mb-4">
              {priceStr}<span className="text-[14px] font-medium text-db-text-2"> one-time</span>
            </div>
          )}
          {!expandedCard &&
          <>
            <button
              onClick={() => handleExpand('one-time')}
              disabled={submitting}
              className="w-full py-3 bg-biscayne rounded-xl text-[14px] font-bold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Book next Training Program
            </button>
          </>}
          {expandedCard === 'one-time' && confirmSection(false)}
        </div>

        {/* OR divider */}
        <div className="flex md:flex-col items-center justify-center flex-shrink-0 px-1">
          <div className="flex-1 h-px md:w-px md:h-auto bg-db-border" />
          <div className="text-[13px] font-bold text-db-text-3 py-2.5">OR</div>
          <div className="flex-1 h-px md:w-px md:h-auto bg-db-border" />
        </div>

        {/* Card B — Recurring */}
        <div className="w-full md:flex-1 bg-white rounded-xl p-5 md:p-[30px_28px] shadow-md border-2 border-db-border flex flex-col relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap bg-biscayne text-white text-[10px] font-extrabold px-3.5 py-1 rounded-full uppercase tracking-[.07em]">
            Recommended
          </div>
          <div className="text-[12px] font-bold uppercase tracking-[.08em] text-db-text-1 mb-3.5">Recurring plan</div>
          <div className="text-[20px] font-extrabold text-db-text-1 mb-1.5">Start a Recurring Plan</div>
          <p className="text-[13px] text-db-text-1 mb-5 leading-relaxed">
            Auto-renew your Training Program every {totalLessons} lessons. Cancel any time.
          </p>
          <div className="flex flex-col gap-2.5 mb-4">
            <div className="flex items-start gap-2.5"><CheckIcon /><span className="text-[13px] text-db-text-1">Everything in one-time, plus…</span></div>
            <div className="flex items-start gap-2.5"><CheckIcon /><span className="text-[13px] font-semibold text-db-text-1">Auto-renews, no re-booking needed</span></div>
            <div className="flex items-start gap-2.5"><CheckIcon /><span className="text-[13px] text-db-text-1">Cancel any time, no penalty</span></div>
          </div>
          <div className="bg-db-bg rounded-xl px-3.5 py-3 border-l-[3px] border-biscayne mb-5">
            <div className="text-[12px] font-bold text-db-text-1 mb-1">Priority Queue Access</div>
            <p className="text-[12px] text-db-text-1 leading-relaxed">
              Recurring plan lessons are reviewed before single-lesson requests, so they typically come back faster.
            </p>
          </div>
          {totalChargeCents > 0 && (
            <div className="text-[26px] font-extrabold text-db-text-1 mb-4">
              {priceStr}<span className="text-[14px] font-medium text-db-text-2"> / bundle</span>
            </div>
          )}
          {!expandedCard &&
          <>
            <button
              onClick={() => handleExpand('recurring')}
              disabled={submitting}
              className="w-full py-3 bg-biscayne rounded-xl text-[14px] font-bold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors mb-2.5"
            >
              Start my recurring plan
            </button>
            <p className="text-center text-[11px] text-db-text-3">Cancel any time · No contracts · No fees</p>
          </>} 
          {expandedCard === 'recurring' && confirmSection(true)}
        </div>

      </div>

      {/* Exit link */}
      <div className="mt-8 text-center">
        <button
          onClick={() => navigate('/discover')}
          className="text-[12px] text-db-text-3 underline hover:text-db-text-2 transition-colors"
        >
          Not ready yet? Explore other coaches
        </button>
      </div>

    </div>
  );
}
